import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, Mail, Phone, MapPin, Linkedin } from 'lucide-react';
import { openCalendlyDemo } from "@/lib/analytics";

const PLATFORM_LINKS = [
  { label: 'AI Email Prevention', to: '/technology/ai-email-prevention' },
  { label: 'Native ERP Integration', to: '/technology/native-erp' },
  { label: 'Multi-Platform', to: '/technology/multi-platform' },
];

const SOLUTION_LINKS = [
  { label: 'Modernize WebCenter AP', to: '/solutions/modernize-oracle-webcenter' },
  { label: '12c End of Support', to: '/solutions/webcenter-12c-end-of-support-decision' },
  { label: '14c Upgrade', to: '/solutions/webcenter-14c-upgrade' },
  { label: 'Forms Recognition Experts', to: '/solutions/webcenter-forms-recognition-experts' },
  { label: 'Modernization Services', to: '/solutions/webcenter-modernization-services' },
  { label: 'Healthcare (Fusion)', to: '/solutions/healthcare-oracle-fusion' },
  { label: 'Education (EBS)', to: '/solutions/education-oracle-ebs' },
  { label: 'See all solutions →', to: '/solutions/modernize-oracle-webcenter' },
];

// WebCenter-specific free tools — separate column in footer to keep
// Company column slim. Labels trimmed to single-line where possible
// since the column heading already says "WebCenter Tools".
const WEBCENTER_LINKS = [
  { label: 'FMW Support Dates', to: '/resources/oracle-fusion-middleware-support-dates' },
  { label: 'Decision Guide', to: '/resources/webcenter-decision-guide' },
  { label: 'Modernization Inspector', to: '/resources/webcenter-content-inspector' },
  { label: 'Component Inventory Pro', to: '/resources/wcc-component-inventory' },
  { label: 'WFR Template Analyzer', to: '/resources/wfr-template-analyzer' },
  { label: 'TCO Calculator', to: '/resources/webcenter-tco-calculator' },
  { label: 'Maturity Benchmark', to: '/resources/ap-maturity-benchmark' },
];

const INTEGRATION_LINKS = [
  { label: 'Oracle', to: '/integrations/oracle' },
  { label: 'NetSuite', to: '/integrations/netsuite' },
  { label: 'SAP', to: '/integrations/sap' },
  { label: 'Dynamics 365', to: '/integrations/dynamics' },
  { label: 'Yardi', to: '/integrations/yardi' },
  { label: 'See all integrations', to: '/integrations' },
];

const INDUSTRY_LINKS = [
  { label: 'Healthcare', to: '/industries/healthcare' },
  { label: 'Property Management', to: '/industries/property-management' },
  { label: 'Manufacturing', to: '/industries/manufacturing' },
  { label: 'Education', to: '/industries/education' },
];

const COMPANY_LINKS = [
  { label: 'About', to: '/about' },
  { label: 'Customers', to: '/customers' },
  { label: 'HACOV Case Study', to: '/customers/hacov' },
  { label: 'Blog', to: '/blog' },
  { label: 'Plans', to: '/plans' },
  { label: 'Conferences', to: '/conferences' },
  { label: 'ROI Calculator', to: '/resources/roi-calculator' },
  { label: 'Privacy Policy', to: '/privacy' },
  { label: 'Accessibility', to: '/accessibility' },
];

const Footer = () => {
  return (
    <footer className="bg-primary border-t border-white/10">
      <div className="container mx-auto px-4 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-x-6 gap-y-8">
          {/* Brand + contact */}
          <div className="col-span-2 lg:col-span-2">
            <div className="text-xl font-bold text-white mb-2">EZ Cloud</div>
            <p className="text-sm text-white/70 mb-6 max-w-sm">
              AI-powered accounts payable automation with native ERP integration. Built for mid-market and enterprise finance teams across North America, the UK, and Australia.
            </p>
            <Button
              variant="accent"
              size="sm"
              onClick={() => openCalendlyDemo()}
            >
              Book a demo
              <ArrowRight className="ml-2 w-4 h-4" aria-hidden />
            </Button>
            <div className="mt-6 space-y-2 text-sm text-white/70">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" aria-hidden />
                <a href="tel:+18568345080" className="hover:text-white">
                  +1 856-834-5080
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4" aria-hidden />
                <a href="mailto:hello@ezcloud.ai" className="hover:text-white">
                  hello@ezcloud.ai
                </a>
              </div>
              <div className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" aria-hidden />
                <span>1134 S Black Horse Pike Suite 266, Blackwood, NJ 08012</span>
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Linkedin className="w-4 h-4" aria-hidden />
                <a
                  href="https://www.linkedin.com/company/ez-cloud-ai/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-white"
                >
                  Follow us on LinkedIn
                </a>
              </div>
            </div>
          </div>

          {/* Platform */}
          <nav aria-labelledby="footer-platform">
            <h2 id="footer-platform" className="text-sm font-semibold text-white mb-3">
              Platform
            </h2>
            <ul className="space-y-2">
              {PLATFORM_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <a
                  href="https://docs.ezcloud.ai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-white/70 hover:text-white transition-colors"
                >
                  Documentation
                </a>
              </li>
            </ul>
          </nav>

          {/* Solutions */}
          <nav aria-labelledby="footer-solutions">
            <h2 id="footer-solutions" className="text-sm font-semibold text-white mb-3">
              Solutions
            </h2>
            <ul className="space-y-2">
              {SOLUTION_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Integrations */}
          <nav aria-labelledby="footer-integrations">
            <h2 id="footer-integrations" className="text-sm font-semibold text-white mb-3">
              Integrations
            </h2>
            <ul className="space-y-2">
              {INTEGRATION_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Industries */}
          <nav aria-labelledby="footer-industries">
            <h2 id="footer-industries" className="text-sm font-semibold text-white mb-3">
              Industries
            </h2>
            <ul className="space-y-2">
              {INDUSTRY_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* WebCenter free tools */}
          <nav aria-labelledby="footer-webcenter">
            <h2 id="footer-webcenter" className="text-sm font-semibold text-white mb-3">
              WebCenter Tools
            </h2>
            <ul className="space-y-2">
              {WEBCENTER_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          {/* Company */}
          <nav aria-labelledby="footer-company">
            <h2 id="footer-company" className="text-sm font-semibold text-white mb-3">
              Company
            </h2>
            <ul className="space-y-2">
              {COMPANY_LINKS.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-xs text-white/70">
            © {new Date().getFullYear()} EZ Cloud. All rights reserved.
          </p>
          <img
            src="/oracle-partner-logo-white.png"
            alt="Oracle Partner"
            width={157}
            height={36}
            className="h-9 w-auto opacity-90"
            loading="lazy"
          />
        </div>
      </div>
    </footer>
  );
};

export default Footer;
